import AppContext from 'App/AppContext';
import { IPIconButton, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import { DialogOk } from 'components/Dialogs/DialogOk';
import AddNewLink from 'components/Links/AddNewLink';
import AutoSaveStatusLabel, { AutoSaveStatus } from 'components/Notification/AutoSaveStatusLabel';
import { TaskFollowUpDialog } from 'components/Tasks/FollowUp/TaskFollowUpDialog';
import TaskTypeForm from 'components/Tasks/Forms/TaskTypeForm';
import SingleTask from 'components/Tasks/SingleTask';
import { hasTaskUpdatePermission } from 'components/Tasks/TaskAuthHelper';
import TaskTopTasksBar from 'components/Tasks/TaskTopTasksBar';
import { AuditExecutionTaskDetail } from 'components/Tasks/TaskTypes/SystemTaskTypes/AuditExecutionTaskDetail';
import {
  globalStackItemStylesPaddingScene,
  globalStackTokensGapSmall,
  leftArrowIcon,
  rightArrowIcon,
} from 'globalStyles';
import { auditCriterionModeOneTime, AuditEntityKey, getAuditEntityKeysOfTask } from 'models/auditCriteria';
import { InternalAuditTaskNavigationStopDTO } from 'models/dto/internalAuditExecutionDTO';
import { EntityTypes } from 'models/entity';
import { AuditStatus } from 'models/internalAudit';
import { auditConclusionMinLength, AuditResultBlock } from 'models/internalAuditExecution';
import KPIData from 'models/kpi/kpiData';
import ResourceLink from 'models/resourceLink';
import EvidenceLocation from 'models/evidenceLocation';
import ResourceList from 'models/resourceList';
import Task, { TaskStateId } from 'models/tasks/task';
import { addTaskContextFrom } from 'models/tasks/taskContext';
import { applyTaskFollowUpSummary, TaskRelation, TaskRelationship } from 'models/tasks/taskRelation';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetEvidenceTaskBindings } from 'services/Api/auditCriteriaService';
import {
  apiGetInternalAuditTaskExecution,
  apiGetInternalAuditTaskNavigation,
  apiGetReadableInternalAuditTask,
  apiGetReadableInternalAuditTaskForm,
} from 'services/Api/internalAuditService';
import { apiGetLinksForId } from 'services/Api/linkService';
import { apiGetSingleTask, apiGetTasksForAuditEntity, apiUpdateTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { newGuid } from 'utils/guid';
import { getAuditTaskHref } from 'utils/internalAudit';

const AUTOSAVE_DEBOUNCE_MS = 1500;

interface AutoSaveOutcome {
  status: 'idle' | 'saved' | 'error';
  error?: string;
}

interface IResourcePanelAuditTasksProps {
  entityType: EntityTypes;
  entityId: number;
  initialTaskId?: number;
  initialInternalAuditId?: number;
  isOpen: boolean;
  onAvailabilityChange: (taskCount: number) => void;
  // a host that cannot move the scene (the document modal) handles the stop itself: it swaps the
  // document for a document stop and opens the scene of an entity stop in a new tab
  onNavigateToStop?: (
    stop: InternalAuditTaskNavigationStopDTO,
    internalAuditId: number | undefined,
  ) => void | Promise<void>;
  // hands the host the autosave flush, so it can await the last debounced save before it reloads
  onRegisterFlush?: (flush: () => Promise<boolean>) => void;
}

export const ResourcePanelAuditTasks = (props: IResourcePanelAuditTasksProps) => {
  const {
    entityType,
    entityId,
    initialTaskId,
    initialInternalAuditId,
    isOpen,
    onAvailabilityChange,
    onNavigateToStop,
    onRegisterFlush,
  } = props;
  //dashboard: is resolved here too (the deleted-item toast), and namespaces load lazily: a key from a
  //namespace this list does not name renders as the raw key
  const { t } = useTranslation(['audit', 'task', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;
  // The auditee's own remaining work as a list of STOPS - one per scope item, not per task. A task
  // covering three controls is three stops on that same task, which is what lets prev/next walk to the
  // next control instead of finding a worklist of one and greying out.
  const [navigationStops, setNavigationStops] = useState<InternalAuditTaskNavigationStopDTO[]>([]);
  const [task, setTask] = useState<Task | undefined>(undefined);
  const [orgTask, setOrgTask] = useState<Task | undefined>(undefined);
  const [isParticipantReadOnly, setIsParticipantReadOnly] = useState<boolean>(false);
  const participantReadOnlyRef = useRef<boolean>(false);
  // opened through the audit and assigned to another user: loaded through the audit, and editable only
  // when the server reports canEdit (a lead auditor or auditor on the audit)
  const [isOtherAssigneesTask, setIsOtherAssigneesTask] = useState<boolean>(false);
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Autosave must not reach the top bar. The debounce fires a save every few keystrokes, and greying
  // Follow-up and Finish out for the length of each of those saves reads as the panel fighting the
  // typing. So only an EXPLICIT action (finish, reopen, follow-up) makes them pending; nothing races,
  // because every one of those handlers flushes the pending autosave before it acts.
  const [isTaskActionPending, setIsTaskActionPending] = useState<boolean>(false);
  const [resultDirty, setResultDirty] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<TaskTypeFormElement[]>([]);
  //changing this makes TaskTypeForm validate and report through onValidate. Without it the form never
  //validated here at all, so Finish completed a task with every required criterion still blank.
  const [showFormErrors, setShowFormErrors] = useState<string | undefined>(undefined);
  const [showFormErrorsDialog, setShowFormErrorsDialog] = useState<boolean>(false);
  //Finish waits for the NEXT onValidate result; holds the taskId whose Finish is pending. A ref, not
  //state: TaskTypeForm validates async, so an effect keyed on the request fires before the errors are
  //in and reads the stale list - which is exactly how a first Finish used to complete a task whose
  //required criteria were still blank. The taskId guards against a switch while validation runs.
  const finishAfterValidationRef = useRef<number | undefined>(undefined);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [autoSaveError, setAutoSaveError] = useState<string | undefined>(undefined);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState<boolean>(false);
  const [followUpTask, setFollowUpTask] = useState<Task | undefined>(undefined);
  const [showUploadEvidenceDialog, setShowUploadEvidenceDialog] = useState<
    { list: ResourceList; location: EvidenceLocation } | undefined
  >(undefined);
  const [resultBlock, setResultBlock] = useState<AuditResultBlock>(AuditResultBlock.NoOutcome);
  const [showResultRequiredDialog, setShowResultRequiredDialog] = useState<boolean>(false);
  const [auditStatus, setAuditStatus] = useState<AuditStatus | undefined>(undefined);
  // The questions of an audit task are the criteria of the entities it audits - here as much as in
  // the task panel itself. So this surface shows the same things: the evidence task a one-time
  // criterion is proved on, and the names to head each entity's questions with.
  const [evidenceTasksByCriterionKey, setEvidenceTasksByCriterionKey] = useState<Map<string, IEvidenceTaskLinkItem[]>>(
    new Map(),
  );
  const [entityNamesByKey, setEntityNamesByKey] = useState<Map<string, string>>(new Map());
  const [evidenceTask, setEvidenceTask] = useState<Task | undefined>(undefined);
  //the auditor took over an AI result: the form fills the answers of that item's questions with the
  //AI's, the pane the outcome and the conclusion. A new request is what applies it
  const [takeOverAiAnswers, setTakeOverAiAnswers] = useState<{ requestId: string; entity: AuditEntityKey } | undefined>(
    undefined,
  );
  const saveResultRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const taskRef = useRef<Task | undefined>(undefined);
  const orgTaskRef = useRef<Task | undefined>(undefined);
  const resultDirtyRef = useRef<boolean>(false);
  const editRevisionRef = useRef<number>(0);
  const saveTimerRef = useRef<number | undefined>(undefined);
  const isSavingRef = useRef<boolean>(false);
  //read from the click handlers, which run outside the render that holds the state
  const isTaskActionPendingRef = useRef<boolean>(false);
  const pendingSaveRef = useRef<boolean>(false);
  const saveLoopPromiseRef = useRef<Promise<AutoSaveOutcome> | undefined>(undefined);
  const hasFormFeature = hasUserFeature(appContext, FeatureTypes.TaskForm);

  taskRef.current = task;
  orgTaskRef.current = orgTask;
  resultDirtyRef.current = resultDirty;
  participantReadOnlyRef.current = isParticipantReadOnly;

  const loadSelectedTask = useCallback(async (taskId: number, providedTask?: Task, providedAccessToken?: string) => {
    const currentAppContext = appContextRef.current;
    const accessToken = providedAccessToken ?? (await currentAppContext.getAccessToken(apiRequest.scopes));
    const selectedTask =
      providedTask ?? (await apiGetSingleTask(taskId, false, accessToken, currentAppContext.globalDataCache)).tasks[0];
    if (!selectedTask) return;

    const taskLinks = selectedTask.resourceLinkIds?.length
      ? await apiGetLinksForId(accessToken, selectedTask.resourceLinkIds, currentAppContext.globalDataCache)
      : [];
    setTask(selectedTask.clone());
    setOrgTask(selectedTask);
    setLinks(taskLinks);
    setResultDirty(false);
    setFormErrors([]);
    //another task starts unvalidated: its own Finish is what asks the new form for its errors
    setShowFormErrors(undefined);
    setResultBlock(AuditResultBlock.NoOutcome);
    setAuditStatus(undefined);
    setAutoSaveStatus('idle');
    setAutoSaveError(undefined);
    setEvidenceTasksByCriterionKey(new Map());
    setTakeOverAiAnswers(undefined);
    editRevisionRef.current = 0;

    // The one-time evidence tasks bound to the criteria this task's form was projected from, so the
    // auditor can open the task that holds the evidence a criterion asks them to inspect. Keyed by
    // criterion key, which is what the projected field carries. Best-effort: without them a field
    // simply shows no link, and nothing is answered for anybody.
    try {
      const rowsPerEntity = await Promise.all(
        getAuditEntityKeysOfTask(selectedTask).map((key) =>
          apiGetEvidenceTaskBindings(key.entityType, key.entityId, accessToken),
        ),
      );

      const byCriterionKey = new Map<string, IEvidenceTaskLinkItem[]>();
      rowsPerEntity.flat().forEach((row) => {
        if (!row.criterionKey || row.mode !== auditCriterionModeOneTime) return;
        const key = row.criterionKey.toLowerCase();
        const tasks = byCriterionKey.get(key) ?? [];
        //one criterion is projected once across the scope entities, so merge the bindings the same way
        if (!tasks.some((item) => item.taskId === row.taskId)) {
          tasks.push({ taskId: row.taskId, taskName: row.taskName });
        }
        byCriterionKey.set(key, tasks);
      });

      setEvidenceTasksByCriterionKey(byCriterionKey);
    } catch {
      //ignore: the links are extra context, never a reason to fail opening the task
    }
  }, []);

  const loadTasks = useCallback(async () => {
    if (!entityId) return;

    try {
      setIsLoading(true);
      const currentAppContext = appContextRef.current;
      const accessToken = await currentAppContext.getAccessToken(apiRequest.scopes);
      const taskIds = await apiGetTasksForAuditEntity(entityType, entityId, accessToken);

      const requestedTaskIsPersonal = initialTaskId !== undefined && taskIds.includes(initialTaskId);
      if (initialTaskId && initialInternalAuditId && !requestedTaskIsPersonal) {
        const readableTask = await apiGetReadableInternalAuditTask(
          initialInternalAuditId,
          initialTaskId,
          entityType,
          entityId,
          accessToken,
          currentAppContext.globalDataCache,
        );
        if (readableTask) {
          // Navigation covers the caller's own tasks only, so another assignee's task is a single stop.
          // The server reports whether the caller may edit it (canEdit).
          const isOtherAssignee = readableTask.userId !== currentAppContext.user.id;
          const execution = isOtherAssignee
            ? await apiGetInternalAuditTaskExecution(initialTaskId, accessToken)
            : undefined;
          const participantReadOnly = isOtherAssignee && !execution?.canEdit;
          const navigation = isOtherAssignee
            ? undefined
            : await apiGetInternalAuditTaskNavigation(initialTaskId, accessToken);
          participantReadOnlyRef.current = participantReadOnly;
          setIsParticipantReadOnly(participantReadOnly);
          setIsOtherAssigneesTask(isOtherAssignee);
          onAvailabilityChange(1);
          setNavigationStops(navigation?.stops ?? [{ entityType, entityId, taskId: initialTaskId }]);
          await loadSelectedTask(initialTaskId, readableTask, accessToken);

          return;
        }
      }

      participantReadOnlyRef.current = false;
      setIsParticipantReadOnly(false);
      setIsOtherAssigneesTask(false);
      onAvailabilityChange(taskIds.length);

      if (taskIds.length === 0) {
        setNavigationStops([]);
        setTask(undefined);
        setOrgTask(undefined);

        return;
      }

      const requestedTaskId = initialTaskId && taskIds.includes(initialTaskId) ? initialTaskId : taskIds[0];
      const navigation = await apiGetInternalAuditTaskNavigation(requestedTaskId, accessToken);
      setNavigationStops(navigation?.stops ?? [{ entityType, entityId, taskId: requestedTaskId }]);
      await loadSelectedTask(requestedTaskId);
    } catch (err) {
      appContextRef.current.setError(err);
      onAvailabilityChange(0);
    } finally {
      setIsLoading(false);
    }
  }, [loadSelectedTask, entityId, entityType, initialTaskId, initialInternalAuditId, onAvailabilityChange]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const openEvidenceTask = async (evidenceTaskId: number) => {
    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetSingleTask(evidenceTaskId, false, accessToken, appContext.globalDataCache);
      if (result.tasks.length > 0) {
        setEvidenceTask(result.tasks[0]);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  // An error the form is showing was computed on the answers of the moment Finish asked for it. Once
  // it is on screen it has to keep up with the answering, or a message stays beside the very field it
  // asked about - demanding the comment that is now written, the evidence that is now attached. The
  // form validates on demand only, so every change to what it validates asks for it again. Same
  // pattern as the task panel (SingleTask); nothing happens before a first Finish.
  const revalidateForm = () => {
    if (showFormErrors) setShowFormErrors(newGuid());
  };

  const updateKpiData = (data: KPIData) => {
    if (participantReadOnlyRef.current || !task) return;
    const updatedTask = task.clone();
    if (!updatedTask.kpiData) updatedTask.kpiData = [];
    const dataIndex = updatedTask.kpiData.findIndex((item) => item.kpiId === data.kpiId);
    if (dataIndex >= 0) updatedTask.kpiData[dataIndex] = data.clone();
    else updatedTask.kpiData.push(data.clone());
    editRevisionRef.current += 1;
    setTask(updatedTask);
    revalidateForm();
  };

  const setKpiData = (data: KPIData | KPIData[]) => {
    if (participantReadOnlyRef.current || !task || !orgTask) return;
    const updatedTask = task.clone();
    if (!updatedTask.kpiData) updatedTask.kpiData = [];
    const dataItems = Array.isArray(data) ? data : [data];
    dataItems.forEach((item) => {
      const dataIndex = updatedTask.kpiData?.findIndex((existing) => existing.kpiId === item.kpiId) ?? -1;
      if (dataIndex >= 0) updatedTask.kpiData![dataIndex] = item.clone();
      else updatedTask.kpiData!.push(item.clone());
    });
    const updatedOrgTask = orgTask.clone();
    updatedOrgTask.kpiData = updatedTask.kpiData.map((item) => item.clone());
    setTask(updatedTask);
    setOrgTask(updatedOrgTask);
  };

  const updateTaskLinks = (newLinks: ResourceLink[]) => {
    if (participantReadOnlyRef.current || !task) return;
    const updatedTask = task.clone();
    updatedTask.resourceLinkIds = newLinks.map((link) => link.linkId);
    editRevisionRef.current += 1;
    setLinks(newLinks);
    setTask(updatedTask);
    revalidateForm();
  };

  const onReportLinkCreated = (link: ResourceLink) => {
    if (participantReadOnlyRef.current || !task || !orgTask || links.some((item) => item.linkId === link.linkId))
      return;
    const newLinks = [...links, link];
    const updatedTask = task.clone();
    updatedTask.resourceLinkIds = newLinks.map((item) => item.linkId);
    const updatedOrgTask = orgTask.clone();
    updatedOrgTask.resourceLinkIds = [...(updatedOrgTask.resourceLinkIds ?? []), link.linkId];
    setLinks(newLinks);
    setTask(updatedTask);
    setOrgTask(updatedOrgTask);
  };

  const applySavedTask = (savedTask: Task) => {
    const savedTaskCopy = savedTask.clone();
    taskRef.current = savedTaskCopy;
    orgTaskRef.current = savedTask;
    resultDirtyRef.current = false;
    setTask(savedTaskCopy);
    setOrgTask(savedTask);
    setResultDirty(false);
    setAutoSaveStatus('saved');
    setAutoSaveError(undefined);
  };

  //the stop we are on is the one for THIS entity and this task: the same task appears once per item
  //it audits, so matching on the task alone would always land on its first stop
  const currentIndex = task
    ? navigationStops.findIndex(
        (stop) => stop.taskId === task.taskId && stop.entityType === entityType && stop.entityId === entityId,
      )
    : -1;
  const isDirty = Boolean(task && orgTask && (!task.isEqual(orgTask) || resultDirty));

  const persistDraft = useCallback(async (): Promise<AutoSaveOutcome> => {
    if (participantReadOnlyRef.current) return { status: 'idle' };
    const currentTask = taskRef.current;
    const currentOrgTask = orgTaskRef.current;
    if (!currentTask || !currentOrgTask) return { status: 'idle' };
    if (currentTask.isEqual(currentOrgTask) && !resultDirtyRef.current) return { status: 'idle' };

    const revision = editRevisionRef.current;
    const taskToSave = currentTask.clone();

    try {
      await saveResultRef.current?.();
      const currentAppContext = appContextRef.current;
      const accessToken = await currentAppContext.getAccessToken(apiRequest.scopes);
      const savedTask = await apiUpdateTask(taskToSave, false, accessToken, currentAppContext.globalDataCache);
      if (!savedTask) return { status: 'error', error: t('audit:Execute.Task.RelatedTasks.AutoSaveFailedDetail') };

      orgTaskRef.current = savedTask;
      setOrgTask(savedTask);
      if (editRevisionRef.current === revision) {
        const savedTaskCopy = savedTask.clone();
        taskRef.current = savedTaskCopy;
        resultDirtyRef.current = false;
        setTask(savedTaskCopy);
        setResultDirty(false);
      } else {
        pendingSaveRef.current = true;
      }

      return { status: 'saved' };
    } catch (err) {
      return { status: 'error', error: AppError.fromApiError(err).message };
    }
  }, [t]);

  const runAutoSave = useCallback(async (): Promise<AutoSaveOutcome> => {
    if (isSavingRef.current) {
      pendingSaveRef.current = true;

      return saveLoopPromiseRef.current ?? { status: 'idle' };
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setAutoSaveStatus('saving');
    const saveLoop = (async (): Promise<AutoSaveOutcome> => {
      let outcome: AutoSaveOutcome = { status: 'idle' };
      do {
        pendingSaveRef.current = false;
        const currentOutcome = await persistDraft();
        if (currentOutcome.status !== 'idle' || outcome.status === 'idle') outcome = currentOutcome;
      } while (pendingSaveRef.current && outcome.status !== 'error');

      isSavingRef.current = false;
      setIsSaving(false);
      setAutoSaveStatus(outcome.status);
      setAutoSaveError(outcome.status === 'error' ? outcome.error : undefined);

      return outcome;
    })();

    saveLoopPromiseRef.current = saveLoop;
    try {
      return await saveLoop;
    } finally {
      saveLoopPromiseRef.current = undefined;
    }
  }, [persistDraft]);

  const runAutoSaveRef = useRef(runAutoSave);
  runAutoSaveRef.current = runAutoSave;

  // Finish asked the form to validate; this is the answer coming back. Errors stop the completion and
  // are named to the auditor, no errors lets it through. Deciding here - in the validation result
  // callback itself - is what makes Finish deterministic: the form validates asynchronously.
  const onFormValidate = (errors: TaskTypeFormElement[]) => {
    setFormErrors(errors);

    const pendingTaskId = finishAfterValidationRef.current;
    if (pendingTaskId === undefined) return;
    finishAfterValidationRef.current = undefined;
    if (pendingTaskId !== taskRef.current?.taskId) return;

    if (errors.length > 0) {
      setShowFormErrorsDialog(true);

      return;
    }

    void completeTask();
  };

  // An unanswered required question is a DRAFT, not a failed save: the server validates the form only
  // on the save that COMPLETES the task, and finishTask validates before it completes. Autosave must
  // therefore keep running while questions are still open - it is the answer typed to clear the error
  // that most needs storing.
  useEffect(() => {
    if (saveTimerRef.current !== undefined) window.clearTimeout(saveTimerRef.current);
    if (!isOpen || !isDirty || isParticipantReadOnly) return;

    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = undefined;
      void runAutoSave();
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current !== undefined) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
      }
    };
  }, [isDirty, isOpen, isParticipantReadOnly, runAutoSave, task, resultDirty]);

  useEffect(
    () => () => {
      if (saveTimerRef.current !== undefined) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
        void runAutoSaveRef.current();
      }
    },
    [],
  );

  // Never refuses over open questions either: navigating away, opening a follow-up and finishing all
  // flush through here, and a refusal dropped exactly the answers the auditor had just typed.
  const flushAutoSave = async (): Promise<boolean> => {
    if (saveTimerRef.current !== undefined) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = undefined;
    }

    if (participantReadOnlyRef.current) return true;

    const outcome = saveLoopPromiseRef.current ? await saveLoopPromiseRef.current : await runAutoSave();

    return outcome.status !== 'error';
  };

  // the flush reads refs only, so a host may call it after this pane has unmounted
  const flushAutoSaveRef = useRef(flushAutoSave);
  flushAutoSaveRef.current = flushAutoSave;

  useEffect(() => {
    onRegisterFlush?.(() => flushAutoSaveRef.current());
  }, [onRegisterFlush]);

  const setTaskActionPending = (pending: boolean) => {
    isTaskActionPendingRef.current = pending;
    setIsTaskActionPending(pending);
  };

  const saveParentRelations = async (updatedTask: Task): Promise<Task | undefined> => {
    if (participantReadOnlyRef.current) return undefined;
    const currentOrgTask = orgTaskRef.current;
    if (!currentOrgTask) return undefined;

    try {
      applyTaskFollowUpSummary(updatedTask);
      setTaskActionPending(true);
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const savedTask = await apiUpdateTask(updatedTask, false, accessToken, appContext.globalDataCache, undefined, {
        includeTaskRelations: true,
        originalTaskRelations: currentOrgTask.taskRelations,
      });
      if (!savedTask) return undefined;

      applySavedTask(savedTask);

      return savedTask;
    } catch (err) {
      appContext.setError(err);

      return undefined;
    } finally {
      setIsSaving(false);
      setTaskActionPending(false);
    }
  };

  const openFollowUp = async () => {
    const currentTask = taskRef.current;
    if (participantReadOnlyRef.current || !currentTask || isTaskActionPendingRef.current) return;
    if (!(await flushAutoSave())) return;
    if (currentTask.hasUnlinkedFollowUp) {
      appContext.showNotification(t('task:FollowUp.AlreadyPending'), false, false, true);

      return;
    }

    setShowFollowUpDialog(true);
  };

  const saveTaskState = async (updatedTask: Task) => {
    if (participantReadOnlyRef.current) return;

    try {
      isSavingRef.current = true;
      setTaskActionPending(true);
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const savedTask = await apiUpdateTask(updatedTask, false, accessToken, appContext.globalDataCache);
      if (!savedTask) {
        appContext.showNotification(t('audit:Execute.Task.RelatedTasks.AutoSaveFailedDetail'), true, false, true);

        return;
      }

      applySavedTask(savedTask);
    } catch (err) {
      appContext.setError(err);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
      setTaskActionPending(false);
    }
  };

  const finishTask = async () => {
    if (participantReadOnlyRef.current || isTaskActionPendingRef.current) return;
    if (resultBlock !== AuditResultBlock.None) {
      setShowResultRequiredDialog(true);

      return;
    }

    // The criteria of a control are required questions, so finishing has to answer them - exactly
    // like the task panel does. Validation runs on demand: asking for it and reading the result takes
    // a render, so this returns and the effect below finishes the job once the errors are in.
    const currentTask = taskRef.current;
    if (hasFormFeature && currentTask?.taskTypeId) {
      finishAfterValidationRef.current = currentTask.taskId;
      setShowFormErrors(newGuid());

      return;
    }

    await completeTask();
  };

  const completeTask = async () => {
    if (!(await flushAutoSave())) return;
    const currentTask = taskRef.current;
    if (!currentTask) return;

    const completedTask = currentTask.clone();
    completedTask.setComplete();
    await saveTaskState(completedTask);
  };

  const reopenTask = async () => {
    const currentTask = taskRef.current;
    if (participantReadOnlyRef.current || !currentTask || isTaskActionPendingRef.current) return;

    const reopenedTask = currentTask.clone();
    reopenedTask.setReopened();
    await saveTaskState(reopenedTask);
  };

  const getResultRequiredText = (): string => {
    switch (resultBlock) {
      case AuditResultBlock.ShortConclusion:
        return t('audit:Execute.Task.ResultRequired.ShortConclusion', { min: auditConclusionMinLength });
      default:
        return t('audit:Execute.Task.ResultRequired.NoOutcome');
    }
  };

  const createFollowUpFromTemplate = async (template: Task) => {
    const currentTask = taskRef.current;
    if (participantReadOnlyRef.current || !currentTask) return;

    try {
      setShowFollowUpDialog(false);
      let newTask = new Task().applyTemplate(template, appContext);
      newTask.taskStates = appContext.globalDataCache.taskStates.items;
      newTask.taskStateId = newTask.getFirstState();
      newTask.taskRelations = [];
      newTask.creator = appContext.user;
      newTask.creatorId = appContext.user.id;
      newTask = await addTaskContextFrom(newTask, currentTask.taskId, appContext);
      setFollowUpTask(newTask);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const createPendingFollowUp = async (notifyUserId?: string) => {
    const currentTask = taskRef.current;
    if (participantReadOnlyRef.current || !currentTask) return;

    setShowFollowUpDialog(false);
    const updatedTask = currentTask.clone();
    updatedTask.hasFollowUp = true;
    updatedTask.hasUnlinkedFollowUp = true;
    const relation = new TaskRelation(currentTask.taskId, null, TaskRelationship.FollowUp);
    relation.notifyUserId = notifyUserId;
    updatedTask.taskRelations = [...updatedTask.taskRelations, relation];
    await saveParentRelations(updatedTask);
  };

  const saveCreatedFollowUp = async (savedTask: Task, _isNew?: boolean, isCanceled?: boolean) => {
    if (participantReadOnlyRef.current) return;

    if (isCanceled) {
      setFollowUpTask(undefined);

      return;
    }

    const currentTask = taskRef.current;
    if (!currentTask) return;
    const updatedTask = currentTask.clone();
    const relation = new TaskRelation(currentTask.taskId, savedTask.taskId, TaskRelationship.FollowUp);
    relation.taskTo = savedTask;
    updatedTask.taskRelations = [...updatedTask.taskRelations, relation];
    const parentTask = await saveParentRelations(updatedTask);
    if (parentTask) setFollowUpTask(undefined);
  };

  const navigate = async (offset: number) => {
    if (navigationStops.length <= 1 || currentIndex < 0) return;
    const nextStop = navigationStops[(currentIndex + offset + navigationStops.length) % navigationStops.length];
    if (!nextStop || !(await flushAutoSave())) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      // A stop names its own item, so the destination never has to be guessed from the task: the next
      // item of a task that audits several is the same task beside another entity, and the scene has
      // to move with it or the panel would show that task's form under the wrong control.
      const execution = await apiGetInternalAuditTaskExecution(nextStop.taskId, accessToken);
      if (onNavigateToStop) {
        await onNavigateToStop(nextStop, execution?.internalAuditId);

        return;
      }

      const href = getAuditTaskHref(
        nextStop.entityType as EntityTypes,
        nextStop.entityId,
        nextStop.taskId,
        execution?.internalAuditId,
      );

      if (href) {
        history.push(href);

        return;
      }

      await loadSelectedTask(nextStop.taskId);
      const navigation = await apiGetInternalAuditTaskNavigation(nextStop.taskId, accessToken);
      setNavigationStops(navigation?.stops ?? [nextStop]);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!task || !orgTask) {
    return (
      <IPStack styles={globalStackItemStylesPaddingScene}>
        <IPText>{t('audit:Execute.Task.RelatedTasks.Empty')}</IPText>
      </IPStack>
    );
  }

  // on another assignee's task the auditor role is the grant, whatever the task's sharing
  const canUpdate = !isParticipantReadOnly && (isOtherAssigneesTask || hasTaskUpdatePermission(orgTask, appContext));
  const hasForm = hasFormFeature && Boolean(task.taskTypeId && task.taskTypeId > 0);

  // Two records, two reasons to freeze, exactly as the task panel decides it (AuditExecutionTask): a
  // completed audit locks its work until it is moved back, and a finished task holds what it was
  // closed with until it is reopened. The SAVED state decides, so picking another status in the panel
  // cannot unlock what the next save would put back.
  const taskFrozen = orgTask.taskStateId === TaskStateId.Completed || orgTask.taskStateId === TaskStateId.Archived;
  const frozen =
    isParticipantReadOnly ||
    !canUpdate ||
    taskFrozen ||
    (auditStatus !== undefined && auditStatus >= AuditStatus.Completed);

  // Criteria are not managed from an execution task: they belong to the procedure and the framework.
  // The empty form only says what the state is, the same line the task pane shows.
  const renderEmptyFormState = (noApplicableCriteria?: boolean) => (
    <IPStack horizontalAlign="center">
      <IPText>
        {noApplicableCriteria
          ? t('audit:Execute.Task.EmptyForm.NoApplicableCriteria')
          : t('audit:Execute.Task.EmptyForm.TextReadOnly')}
      </IPText>
    </IPStack>
  );

  const leadingCommands = (
    <IPIconButton
      iconProps={leftArrowIcon}
      title={t('translation:General.Button.Prev')}
      disabled={navigationStops.length <= 1 || currentIndex < 0 || isSaving}
      onClick={() => void navigate(-1)}
    />
  );
  const trailingCommands = (
    <>
      <AutoSaveStatusLabel
        status={autoSaveStatus}
        savingText={t('audit:Execute.Task.RelatedTasks.AutoSaving')}
        savedText={t('audit:Execute.Task.RelatedTasks.AutoSaved')}
        errorText={t('audit:Execute.Task.RelatedTasks.AutoSaveFailed')}
        errorDetail={autoSaveError ?? t('audit:Execute.Task.RelatedTasks.AutoSaveFailedDetail')}
      />
      <IPIconButton
        iconProps={rightArrowIcon}
        title={t('translation:General.Button.Next')}
        disabled={navigationStops.length <= 1 || currentIndex < 0 || isSaving}
        onClick={() => void navigate(1)}
      />
    </>
  );

  return (
    <Fragment>
      <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { minHeight: 0, overflow: 'hidden' } }}>
        <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
          <AuditExecutionTaskDetail
            task={task}
            orgTask={orgTask}
            getErrorMessage={() => undefined}
            isActionPending={isSaving}
            canUpdate={canUpdate}
            readOnly={isParticipantReadOnly}
            compact
            compactActions={
              // canSave is NOT the last validation result. That result is only refreshed when Finish
              // asks the form to validate, so gating the bar on it greyed Finish out for good after the
              // first failed attempt - including the very click that would validate again. finishTask
              // validates on every click and refuses to complete there; this bar only has to be usable.
              !isParticipantReadOnly ? (
                <TaskTopTasksBar
                  task={task}
                  orgTask={orgTask}
                  compact
                  canUpdate={canUpdate}
                  canDelete={false}
                  canSave
                  isLoading={isLoading}
                  isActionPending={isTaskActionPending}
                  onUploadEvidence={(list, location) => setShowUploadEvidenceDialog({ list, location })}
                  onClickFollowUp={() => void openFollowUp()}
                  onClickFinish={() => void finishTask()}
                  onClickReopen={
                    auditStatus !== undefined && auditStatus < AuditStatus.Completed
                      ? () => void reopenTask()
                      : undefined
                  }
                />
              ) : undefined
            }
            compactTaskContent={
              /* The audit's questions live on the FORM, here as much as in the task panel: a
                 procedure that asks the control's criteria projects them there, and the checklist
                 that used to stand in for them is not where anybody answers any more. A form that
                 asks nothing yet says so. */
              hasForm ? (
                <TaskTypeForm
                  isOpen
                  task={task}
                  links={links}
                  compact
                  compactTaskView
                  readonly={frozen}
                  loadFormAndData={
                    isOtherAssigneesTask && initialInternalAuditId
                      ? (accessToken) =>
                          apiGetReadableInternalAuditTaskForm(
                            initialInternalAuditId,
                            task.taskId,
                            entityType,
                            entityId,
                            accessToken,
                          )
                      : undefined
                  }
                  showErrors={showFormErrors}
                  onUpdateData={updateKpiData}
                  onSetData={setKpiData}
                  onUpdateTaskForForm={(updatedTask, validateForm) => {
                    if (participantReadOnlyRef.current) return;
                    editRevisionRef.current += 1;
                    setTask(updatedTask);
                    if (validateForm) revalidateForm();
                  }}
                  addLinks={(newLinks) => updateTaskLinks([...links, ...newLinks])}
                  setLinks={updateTaskLinks}
                  removeLink={(link) => updateTaskLinks(links.filter((item) => item.linkId !== link.linkId))}
                  onValidate={onFormValidate}
                  renderEmptyState={renderEmptyFormState}
                  evidenceTasksByCriterionKey={evidenceTasksByCriterionKey}
                  onOpenEvidenceTask={(evidenceTaskId) => void openEvidenceTask(evidenceTaskId)}
                  entityNamesByKey={entityNamesByKey}
                  takeOverAiAnswers={takeOverAiAnswers}
                />
              ) : (
                renderEmptyFormState()
              )
            }
            leadingCommands={leadingCommands}
            trailingCommands={trailingCommands}
            onResultBlockChange={setResultBlock}
            onResultDirtyChange={(dirty) => {
              if (isParticipantReadOnly) return;
              if (dirty) editRevisionRef.current += 1;
              setResultDirty(dirty);
            }}
            onRegisterSaveResult={(saveResult) => {
              saveResultRef.current = isParticipantReadOnly ? undefined : saveResult;
            }}
            onAuditStatusChange={setAuditStatus}
            onEntityNamesChange={setEntityNamesByKey}
            onFlushDraft={flushAutoSave}
            onReportLinkCreated={isParticipantReadOnly ? undefined : onReportLinkCreated}
            onTakeOverAiAnswers={
              isParticipantReadOnly ? undefined : (entity) => setTakeOverAiAnswers({ requestId: newGuid(), entity })
            }
          />
        </IPStack.Item>
      </IPStack>
      <TaskFollowUpDialog
        isOpen={showFollowUpDialog}
        onClose={() => setShowFollowUpDialog(false)}
        onSave={(template) => void createFollowUpFromTemplate(template)}
        onSkip={(notifyUserId) => void createPendingFollowUp(notifyUserId)}
      />
      {!isParticipantReadOnly && showUploadEvidenceDialog && task.isWorkableTask() && (
        <AddNewLink
          hideButton
          selectedList={showUploadEvidenceDialog.list}
          uploadFolderId={showUploadEvidenceDialog.location.folderDriveItemId}
          uploadFolderName={showUploadEvidenceDialog.location.folderName}
          existingLinks={links}
          addLinks={(newLinks) => updateTaskLinks([...links, ...newLinks])}
          allowCustomLists
          allowDocumentLibraries
          allowSitePages
          allowWebURLs
          close={() => setShowUploadEvidenceDialog(undefined)}
          disabled={false}
          hideWholeList
          entityContext
        />
      )}
      <DialogOk
        onOk={() => setShowResultRequiredDialog(false)}
        title={t('audit:Execute.Task.ResultRequired.Title')}
        subText={getResultRequiredText()}
        hidden={!showResultRequiredDialog}
      />
      <DialogOk
        onOk={() => setShowFormErrorsDialog(false)}
        title={t('task:ShowFormErrorsDialog.Title')}
        subText={t('task:ShowFormErrorsDialog.SubText', {
          name: task.formVersion?.name ?? (task.taskTypeId ? appContext.globalDataCache.taskTypes.get(task.taskTypeId).name : ''),
          count: formErrors.length,
        })}
        hidden={!showFormErrorsDialog}
      />
      {followUpTask && (
        <SingleTask
          isOpen
          task={followUpTask}
          close={() => setFollowUpTask(undefined)}
          onSave={saveCreatedFollowUp}
          onRemove={() => setFollowUpTask(undefined)}
          onUpdate={() => setFollowUpTask(undefined)}
          disallowSchedule={false}
          windowLevel={1}
        />
      )}
      {/* The task a one-time criterion is proved on: opened next to the audit task, so the auditor
          reads the evidence without losing what they were answering. Reopen, a completion-date change
          and removing it from Outlook all report through onUpdate and deliberately keep the window
          open - the auditor is still reading it - so that one hands the task back instead of closing. */}
      {evidenceTask && (
        <SingleTask
          isOpen
          task={evidenceTask}
          close={() => setEvidenceTask(undefined)}
          onSave={() => setEvidenceTask(undefined)}
          onRemove={() => setEvidenceTask(undefined)}
          onUpdate={(updated) => setEvidenceTask(updated)}
          disallowSchedule={false}
          windowLevel={1}
        />
      )}
    </Fragment>
  );
};
